import { portalFehlerausgang, readPortalDiagContext } from './portalFehlerpaket';

/** Nutzerfreundliche Portal-Fehlertexte (DE) — technische Details nur in der Konsole. */

function httpStatusFromPortalError(context: string, detail: unknown): number | null {
  if (typeof detail === 'number' && Number.isInteger(detail)) return detail;
  if (detail && typeof detail === 'object' && 'status' in detail) {
    const status = (detail as { status?: unknown }).status;
    if (typeof status === 'number' && Number.isInteger(status)) return status;
  }
  const match = context.match(/\bHTTP\s+(\d{3})\b/);
  return match ? Number(match[1]) : null;
}

export function logPortalError(context: string, detail: unknown): void {
  console.error(`[ARKORBIT Portal] ${context}`, detail);
  const status = httpStatusFromPortalError(context, detail);
  const ctx = readPortalDiagContext();
  const traced = {
    ...(ctx.requestId ? { requestId: ctx.requestId } : {}),
    ...(ctx.lastDiagStep ? { lastDiagStep: ctx.lastDiagStep } : {}),
    ...(ctx.qualityGate ? { qualityGate: ctx.qualityGate } : {}),
  };
  if (status == null) {
    if (context !== 'Netzwerk') return;
    portalFehlerausgang({ ...traced, consoleLines: ['Netz'] });
    return;
  }
  portalFehlerausgang({
    ...traced,
    status,
    consoleLines: [`HTTP ${status}`],
  });
}

/** Erwartbare Fachantworten (HTTP 200/202, Pending-OCR) — kein rotes F12. */
export function logPortalWarn(context: string, detail: unknown): void {
  console.warn(`[ARKORBIT Portal] ${context}`, detail);
}

export function logPortalExtractOutcome(detail: {
  status: number;
  error?: string;
  pending?: boolean;
}): void {
  if (detail.pending || detail.status === 202) return;
  if (detail.status === 200) {
    logPortalWarn('OnlyOffice-Extract', detail);
    return;
  }
  logPortalError('OnlyOffice-Extract', detail);
}

/** Erwartbare 403: gleiche Kanzlei fehlt am Legacy-Record — kein IT-Alarm in F12. */
export function isExpectedPortalAktenKanzleiDeny(status: number, message?: string): boolean {
  if (status !== 403) return false;
  return /Akte gehört nicht zu dieser Kanzlei/i.test(String(message ?? ''));
}

export function formatHttpStatusForUser(
  status: number,
  context: 'upload' | 'import' | 'api' | 'editor' | 'download' | 'generic' = 'generic',
): string {
  if (status === 401 || status === 403) {
    return 'Ihre Sitzung ist abgelaufen oder Sie haben keine Berechtigung. Bitte neu anmelden und erneut versuchen.';
  }
  if (status === 404) {
    if (context === 'editor') {
      return 'Der Dokumenten-Editor ist derzeit nicht verfügbar. Bitte später erneut versuchen oder ARKORBIT informieren.';
    }
    return 'Der angeforderte Dienst wurde nicht gefunden. Bitte ARKORBIT informieren, falls das Problem bleibt.';
  }
  if (status === 405) {
    return 'Die Server-Konfiguration blockiert diese Aktion. Bitte ARKORBIT informieren.';
  }
  if (status === 429) {
    return 'Zu viele Anfragen in kurzer Zeit. Bitte einen Moment warten und erneut versuchen.';
  }
  if (status === 502 || status === 503 || status === 504) {
    const verb =
      context === 'upload'
        ? 'speichern'
        : context === 'import'
          ? 'importieren'
          : context === 'editor'
            ? 'öffnen'
            : context === 'download'
              ? 'laden'
              : 'verarbeiten';
    return `Der Server ist vorübergehend nicht erreichbar. Bitte in ein paar Sekunden erneut ${verb}.`;
  }
  if (status >= 500) {
    return 'Ein Serverfehler ist aufgetreten. Bitte erneut versuchen. Bleibt das Problem, ARKORBIT informieren.';
  }
  return 'Die Anfrage konnte nicht verarbeitet werden. Bitte Eingabe prüfen und erneut versuchen.';
}

export function formatPortalUploadError(
  res: Response,
  data: Record<string, unknown>,
  action: 'upload' | 'import' = 'upload',
): string {
  if (typeof data.error === 'string' && data.error.trim() && !/^HTTP\s+\d+/i.test(data.error)) {
    return data.error.trim();
  }
  if (!res.ok) {
    logPortalError(`${action} HTTP ${res.status}`, data);
    return formatHttpStatusForUser(res.status, action);
  }
  return 'Die Antwort vom Server war unvollständig. Bitte erneut versuchen.';
}

export function formatOnlyofficeJwtErrorForUser(res: Response, errStr?: string): string {
  logPortalError(`OnlyOffice JWT HTTP ${res.status}`, errStr ?? res.statusText);
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    return 'Der Dokumenten-Editor kann gerade nicht gestartet werden — Verbindung zum Server fehlgeschlagen. Bitte kurz warten und erneut öffnen.';
  }
  if (res.status === 405) {
    return 'Der Dokumenten-Editor ist auf diesem Server noch nicht korrekt eingerichtet. Bitte ARKORBIT informieren.';
  }
  if (res.status === 401 || res.status === 403) {
    return 'Keine Berechtigung zum Öffnen des Dokuments. Bitte neu anmelden.';
  }
  if (errStr && !/^HTTP\s+\d+/i.test(errStr) && !errStr.includes('JWT-Webhook')) {
    return errStr;
  }
  return formatHttpStatusForUser(res.status, 'editor');
}

export function formatConnectionError(context?: string): string {
  logPortalError('Netzwerk', context ?? 'fetch failed');
  return 'Keine Verbindung zum Server. Bitte Internetverbindung prüfen und erneut versuchen.';
}

/** Firma/Demo ohne `portal_akten` — nie PocketBase-Englisch in die UI. */
export const PORTAL_AKTEN_COLLECTION_MISSING_DE =
  'Die Akten-Ablage ist auf diesem Server noch nicht eingerichtet. ' +
  'Ihre Angaben bleiben im Chat. Bitte ARKORBIT informieren, wenn Akten hier erwartet werden.';

export function isPbCollectionContextError(status: number, message?: string): boolean {
  const msg = String(message ?? '');
  if (/missing or invalid collection context/i.test(msg)) return true;
  if (/collection .+ not found/i.test(msg)) return true;
  if (/didn't find the requested resource/i.test(msg)) return true;
  return status === 404;
}

export function formatPortalAktenPbError(
  status: number,
  message?: string,
): { error: string; collectionMissing: boolean } {
  const raw = String(message ?? '').trim();
  if (isPbCollectionContextError(status, raw)) {
    return { error: PORTAL_AKTEN_COLLECTION_MISSING_DE, collectionMissing: true };
  }
  if (/failed to create record/i.test(raw)) {
    return {
      error: 'Die Akte konnte nicht angelegt werden. Bitte Eingaben prüfen oder später erneut versuchen.',
      collectionMissing: false,
    };
  }
  if (/something went wrong/i.test(raw)) {
    return {
      error: 'Die Akte konnte gerade nicht gespeichert werden. Bitte kurz warten und erneut versuchen.',
      collectionMissing: false,
    };
  }
  const looksEnglishTechnical =
    /\b(invalid|missing|failed|unauthorized|forbidden|not found|collection|record|context)\b/i.test(raw) &&
    !/[äöüÄÖÜß]/.test(raw);
  if (looksEnglishTechnical) {
    return { error: formatHttpStatusForUser(status || 400, 'api'), collectionMissing: false };
  }
  if (raw && /[äöüÄÖÜß]/.test(raw)) {
    return { error: raw, collectionMissing: false };
  }
  if (raw && !/^HTTP\s+\d+$/i.test(raw) && !looksEnglishTechnical) {
    return { error: raw, collectionMissing: false };
  }
  return { error: formatHttpStatusForUser(status || 400, 'api'), collectionMissing: false };
}
